import { parseDelimitedTable } from 'features/chat/utils/assistant-file-preview';
import { ConfigurationSettings } from 'models/configuration-settings';
import {
  getFileBlobUrl,
  openPdfInNewTab,
  resolveSourceFileOpenUrl,
} from 'services/storage-service';

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const buildCsvPreviewHtml = (text: string) => {
  const table = parseDelimitedTable(text, 'csv');
  const body = table
    ? `<table><thead><tr>${table.headers.map((header) => `<th>${escapeHtml(header)}</th>`).join('')}</tr></thead><tbody>${table.rows
        .map((row) => `<tr>${row.map((cell) => `<td>${escapeHtml(cell)}</td>`).join('')}</tr>`)
        .join('')}</tbody></table>`
    : `<pre>${escapeHtml(text)}</pre>`;

  return `<!DOCTYPE html><html><head><title>CSV</title><style>html,body{margin:0;padding:24px;font-family:sans-serif}table{border-collapse:collapse;width:100%}th,td{border-bottom:1px solid #e7e7e7;padding:8px 12px;text-align:left;white-space:nowrap}th{background:#f5f5f5}</style></head><body>${body}</body></html>`;
};

const openViewerInNewTab = async (
  fileId: string,
  config: ConfigurationSettings,
  buildUrl: (openUrl: string) => string
) => {
  const tab = window.open('about:blank', '_blank');
  try {
    const { openUrl } = await resolveSourceFileOpenUrl(fileId, config, undefined);
    if (openUrl && tab && !tab.closed) {
      tab.location.replace(buildUrl(openUrl));
    } else {
      tab?.close();
    }
  } catch (error) {
    tab?.close();
    throw error;
  }
};

const openCsvInNewTab = async (fileId: string, config: ConfigurationSettings) => {
  const tab = window.open('about:blank', '_blank');
  try {
    const blobUrl = await getFileBlobUrl(fileId, config, undefined);
    if (!blobUrl) {
      tab?.close();
      return;
    }

    const text = await fetch(blobUrl).then((response) => response.text());
    if (!tab || tab.closed) {
      return;
    }

    tab.document.open();
    tab.document.write(buildCsvPreviewHtml(text));
    tab.document.close();
  } catch (error) {
    tab?.close();
    throw error;
  }
};

export const openMediaLibraryFile = (
  file: { id: string | number; fileType: string },
  config: ConfigurationSettings
) => {
  const fileId = file.id.toString();

  if (file.fileType === 'Pdf') {
    return openPdfInNewTab(fileId, config, undefined);
  }

  if (file.fileType === 'Csv') {
    return openCsvInNewTab(fileId, config);
  }

  if (file.fileType === 'Xlsx' || file.fileType === 'Spreadsheet') {
    return openViewerInNewTab(
      fileId,
      config,
      (openUrl) => `https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(openUrl)}`
    );
  }

  if (file.fileType === 'Document' || file.fileType === 'Pptx') {
    return openViewerInNewTab(
      fileId,
      config,
      (openUrl) => `https://docs.google.com/gview?url=${encodeURIComponent(openUrl)}&embedded=true`
    );
  }

  return Promise.resolve();
};
